import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { sessionService } from '../../application/SessionService';
import { OrganizationActionType } from '../../context/organization/actions';
import { useOrganizationContext } from '../../context/organization/context';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { UserActionType } from '../../context/user/actions';
import { useUserContext } from '../../context/user/context';
import { UserAuthActionType } from '../../pages/auth/login/context/actions';
import { useUserAuthContext } from '../../pages/auth/login/context/context';
import { TwoFactorMethod } from '../../pages/auth/login/context/reducer';
import eventsPanel from '../../services/events';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseCreateSessionProps {
  email: string;
  password: string;
}

type UseCreateSessionResponseProps = ServiceResponse<() => void, void> & {
  isLogged: boolean;
  notValidatedEmail?: string;
  hasTwoFactorMethods?: boolean;
  phone?: string;
  reset: () => void;
};

// Hooks act as 'Adapter' layer.
export const useCreateSession = ({ email, password }: UseCreateSessionProps): UseCreateSessionResponseProps => {
  const queryClient = useQueryClient();
  const { organizationDispatch } = useOrganizationContext();
  const { userDispatch } = useUserContext();
  const { projectDispatch } = useProjectContext();
  const { userAuthDispatch } = useUserAuthContext();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const [notValidatedEmail, setNotValidatedEmail] = useState<string | undefined>(undefined);

  const { data, isRefetching, isError, isSuccess, refetch, remove } = useQuery<{
    twoFactorMethods: TwoFactorMethod[];
    phone?: string;
  }>([`createSession`], () => sessionService.createSession({ email: email.toLowerCase(), password }), {
    enabled: false,
    retry: false,
    refetchOnWindowFocus: false,

    onError: (err: any) => {
      if (err?.response?.status === 401 && err.response.data?.messageId === `highway.user.error.not_validated`) {
        setNotValidatedEmail(email.toLowerCase());
        return;
      }

      let messageIntlId = `infoerror.unexpectedresult`;

      if (err?.response?.status === 400 || err?.response?.status === 401) {
        messageIntlId = `infoerror.highway.user.error.invalid`;
      } else if (err?.response?.data?.messageId) {
        messageIntlId = `infoerror.${err.response.data.messageId}`;
      }

      showSnackbarMessage({
        messageIntlId,
        autoHideDuration: err.response && err.response.status === 401 ? undefined : 5000,
      });
    },
    onSuccess: async ({ twoFactorMethods, phone }: { twoFactorMethods: TwoFactorMethod[]; phone?: string }) => {
      if (twoFactorMethods.length > 0) {
        userAuthDispatch({
          type: UserAuthActionType.SET_2FA_METHODS,
          payload: { twoFactorMethods: twoFactorMethods },
        });

        return;
      }

      userAuthDispatch({
        type: UserAuthActionType.RESET_USER_AUTH,
      });

      eventsPanel.login();

      // Cleaning the possible previous session data ==== (from here)
      organizationDispatch({
        type: OrganizationActionType.RESET_ORGANIZATION,
      });
      userDispatch({
        type: UserActionType.RESET_USER,
      });
      projectDispatch({
        type: ProjectActionType.RESET_PROJECT,
      });
      await queryClient.invalidateQueries();
      // Cleaning the possible previous session data ==== (from to here)
    },
  });

  return {
    error: isError,
    success: isSuccess,
    isLogged: sessionService.isLogged(),
    isLoading: isRefetching,
    run: refetch,
    reset: remove,
    notValidatedEmail,
    hasTwoFactorMethods: !!data?.twoFactorMethods.length,
    phone: data?.phone,
  };
};
